import { DragDropModule } from '@angular/cdk/drag-drop';
import { APP_BASE_HREF, DatePipe, registerLocaleData } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import localePt from '@angular/common/locales/fr-CA';
import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';
import { ErrorInterceptor } from './@core/helpers/error.interceptor';
import { JwtInterceptor } from './@core/helpers/jwt.interceptor';
import { ApiProvider } from './@core/providers/api.provider';
import { TpslRouteStateProvider } from './@core/providers/tpslroute.state.provider';
import { LogService } from './@core/services/logger.service';
import { AddExceptionDialogComponent } from './@theme/components';
import { DriverDialogComponent } from './@theme/components/driver-dialog/driver-dialog.component';
import { ParcelHistoryDialogComponent } from './@theme/components/parcel-history-dialog/parcel-history-dialog.component';
import {
  CapitalizePipe,
  EvaIconsPipe,
  NumberWithCommasPipe,
  PluralPipe,
  RoundPipe,
  TimingPipe
} from './@theme/pipes';
import { AngularMaterialModule } from './angular-material.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FirebaseModule } from './firebase.module';
import { AuthComponent } from './pages/auth/auth.component';
import { AuthModule } from './pages/auth/auth.module';
import { createTranslateLoader, SharedModule } from './shared.module';
import { UserModule } from './user.module';
import { WebsiteModule } from './Website/website.module';
import { NotificationService } from './@core/utils/notification.service';
import { RouterModule } from '@angular/router';
import { WebsiteRoutes } from './Website/website.routes';
import { LoadingService } from './@core/services/loading.service';
import { ProgressSpinnerComponent } from './@theme/components/progress-spinner/progress-spinner.component';
import { OptimizeDialogComponent } from './@theme/components/optimize-dialog/optimize-dialog.component';
import { UserPreferencesDialogComponent } from './@theme/components/user-preferences-dialog/user-preferences-dialog.component';
import { OptimizeresultDialogComponent } from './@theme/components/optimizeresult-dialog/optimizeresult-dialog.component';
import { MatSpinner } from '@angular/material';
import { TransfertSectorDialogComponent } from './@theme/components/transfert-sector-dialog/transfert-sector-dialog.component';
import { ChangePriorityModalComponent } from './@theme/components/change-priority-modal/change-priority-modal.component';
import { ConfirmDialogComponent } from './@theme/components/confirm-dialog/confirm-dialog.component';
import { TransfertDateDialogComponent } from './@theme/components/transfert-date-dialog/transfert-date-dialog.component';
import { ClientsModalComponent } from './@theme/components/clients-modal/clients-modal.component';
import { SignatureDialogComponent } from './@theme/components/signature-dialog/signature-dialog.component';
import { PhotoService } from './@core/services/photo.service';
import { SignatureService } from './@core/services/signature.service';
import { ShipmentService } from './@core/services/shipment.service';
import { CreatePickupComponent } from './@theme/components/create-pickup/create-pickup.component';
import { TextDialogComponent } from './@theme/components/text-dialog/text-dialog.component';
import { AuthenticationService } from './@core/services/authentication.service';
import { TokenJwtService } from './@core/services/tokenJwt.service';
import { SelectGeotabUser } from './@theme/components/select-geotab-user/select-geotab-user.component';
import { CryptoService } from './@core/services/encrypt.service';
import { NewTpslRouteModal } from './@theme/components/new-tpslRoute-modal/new-tpslRoute-modal.component';
import { ProblemReportDialogComponent } from './@theme/components/problem-report-dialog/problem-report-dialog.component';
import { ProblemReportLoginDialogComponent } from './@theme/components/problem-report-dialog/problem-report-login-dialog.component';

registerLocaleData(localePt);

@NgModule({
  declarations: [
    AppComponent,
    AuthComponent,
    AddExceptionDialogComponent,
    OptimizeDialogComponent,
    CapitalizePipe,
    EvaIconsPipe,
    PluralPipe,
    RoundPipe,
    TimingPipe,
    NumberWithCommasPipe,
    ParcelHistoryDialogComponent,
    SignatureDialogComponent,
    ChangePriorityModalComponent,
    DriverDialogComponent,
    ClientsModalComponent,
    TransfertSectorDialogComponent,
    TransfertDateDialogComponent,
    UserPreferencesDialogComponent,
    ProgressSpinnerComponent,
    OptimizeresultDialogComponent,
    ConfirmDialogComponent,
    CreatePickupComponent,
    TextDialogComponent,
    SelectGeotabUser,
    NewTpslRouteModal,
    ProblemReportDialogComponent,
    ProblemReportLoginDialogComponent
  ],
  imports: [
    BrowserAnimationsModule,
    SharedModule,
    AngularEditorModule,
    HttpClientModule,
    FlexLayoutModule,
    AppRoutingModule,
    RouterModule,
    RouterModule.forChild(WebsiteRoutes),
    AuthModule,
    FirebaseModule,
    FormsModule,
    WebsiteModule,
    UserModule,
    ToastrModule.forRoot(),
    AngularMaterialModule,
    DragDropModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    })
  ],
  exports: [SharedModule, RouterModule],
  entryComponents: [
    AddExceptionDialogComponent,
    OptimizeDialogComponent,
    ParcelHistoryDialogComponent,
    SignatureDialogComponent,
    ChangePriorityModalComponent,
    DriverDialogComponent,
    ClientsModalComponent,
    TransfertSectorDialogComponent,
    TransfertDateDialogComponent,
    UserPreferencesDialogComponent,
    ProgressSpinnerComponent,
    OptimizeresultDialogComponent,
    ConfirmDialogComponent,
    MatSpinner,
    CreatePickupComponent,
    TextDialogComponent,
    SelectGeotabUser,
    NewTpslRouteModal,
    ProblemReportDialogComponent,
    ProblemReportLoginDialogComponent
  ],
  bootstrap: [AppComponent],
  providers: [
    NotificationService,
    LoadingService,
    ApiProvider,
    PhotoService,
    SignatureService,
    TpslRouteStateProvider,
    ShipmentService,
    DatePipe,
    LogService,
    TokenJwtService,
    CryptoService,
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: LOCALE_ID, useValue: 'fr-CA' },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {}
